import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import _ from 'lodash'

import Select from '../Select/Select'
import Api from 'utils/ApiRequest'
import API_URL from 'constants/api'

const ALL = 'all'

const DEFAULT_PARAMS = {
  level: 2,
  sectionId: 0,
  subSectionId: 0,
  typeId: 0,
  subTypeId: 0,
  limit: 50,
  offset: 0,
}

class HierarchySelect extends Component {
  static propTypes = {
    concatId: PropTypes.bool,
    field: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    query: PropTypes.object,
    translate: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    clearButton: PropTypes.bool,
    disabled: PropTypes.bool,
    isMulti: PropTypes.bool,
    allOption: PropTypes.bool,
    className: PropTypes.string,
    selected: PropTypes.bool,
    noOptions: PropTypes.bool,
    value: PropTypes.any,
    styles: PropTypes.object,
    label: PropTypes.string,
    required: PropTypes.bool,
    fill: PropTypes.bool,
  }

  state = {
    options: [],
    error: null,
    loading: true,
  }

  componentDidMount() {
    const { selected } = this.props

    if (selected) {
      this.fetchDataSelected()
    } else {
      this.fetchData()
    }
  }

  componentDidUpdate(prevProps) {
    const { query = {}, selected } = this.props

    if (!_.isEqual(query, prevProps.query)) {
      if (selected && query) {
        this.fetchDataSelected()
      } else {
        this.fetchData()
      }
    }
  }

  fetchDataSelected = async () => {
    const { query, user } = this.props

    this.setState({ options: [] })

    switch (query.level) {
      case 2:
        const { data } = await this.callApi(user.dsToken, { ...DEFAULT_PARAMS })

        this.setState((state) => ({ options: [...state.options, ...data] }))
        break
      case 3:
        if (query.sectionId) {
          await Promise.all(
            query.sectionId.map(async (sectionIdValues) => {
              const params = {
                ...DEFAULT_PARAMS,
                level: query.level,
                sectionId: sectionIdValues.sectionId,
              }

              const { data } = await this.callApi(user.dsToken, params)

              this.setState((state) => ({
                options: [...state.options, ...data],
              }))
            })
          )
        }
        break
      case 4:
        if (query.sectionId && query.subSectionId) {
          await Promise.all(
            query.sectionId.map(async (sectionIdValues) =>
              query.subSectionId.map(async (subSectionIdValues) => {
                const params = {
                  ...DEFAULT_PARAMS,
                  level: query.level,
                  sectionId: sectionIdValues.sectionId,
                  subSectionId: subSectionIdValues.subSectionId,
                }

                const { data } = await this.callApi(user.dsToken, params)

                this.setState((state) => ({
                  options: [...state.options, ...data],
                }))
              })
            )
          )
        }
        break
      case 5:
        if (query.sectionId && query.subSectionId && query.typeId) {
          await Promise.all(
            query.sectionId.map(async (sectionIdValues) =>
              query.subSectionId.map(async (subSectionIdValues) =>
                query.typeId.map(async (typeIdValues) => {
                  const params = {
                    ...DEFAULT_PARAMS,
                    level: query.level,
                    sectionId: sectionIdValues.sectionId,
                    subSectionId: subSectionIdValues.subSectionId,
                    typeId: typeIdValues.typeId,
                  }

                  const { data } = await this.callApi(user.dsToken, params)

                  this.setState((state) => ({
                    options: [...state.options, ...data],
                  }))
                })
              )
            )
          )
        }
        break
      default:
        break
    }

    this.setState({ loading: false })
  }

  fetchData = async () => {
    const { user, query = {} } = this.props

    if (user) {
      const params = {
        level: query.level || 2,
        sectionId: query.sectionId || 0,
        subSectionId: query.subSectionId || 0,
        typeId: query.typeId || 0,
        subTypeId: query.subTypeId || 0,
        limit: 50,
        offset: 0,
      }

      try {
        const { data } = await this.callApi(user.dsToken, params)

        this.onFetchSuccess(data)
      } catch (error) {
        this.onFetchError(error)
      }
    }
  }

  callApi = (token, params) => {
    this.setState({ loading: true })

    return new Api(token).post(API_URL.SECTIONS_HIERARCHY, params)
  }

  onFetchSuccess = (options) => {
    // Remover duplicatas considerando todos os níveis da hierarquia,
    // caso contrário subSection/type/subType com o mesmo sectionId pai
    // seriam descartados e apenas o primeiro item apareceria.
    const uniqueOptions = options.filter((option, index, self) =>
      index === self.findIndex((o) =>
        o.sectionId === option.sectionId &&
        o.subSectionId === option.subSectionId &&
        o.typeId === option.typeId &&
        o.subTypeId === option.subTypeId
      )
    )
    this.setState({ options: uniqueOptions, loading: false, error: null })
  }

  onFetchError = (error) => {
    this.setState({ error, loading: false, options: [] })
  }

  renderOptions = () => {
    const {
      concatId,
      field = 'sectionId',
      allOption = false,
      translate,
    } = this.props

    let options

    if (concatId) {
      options = this.state.options
        .map((option) => ({
          value: option[field],
          label: `${option[field]} - ${option.description}`,
          level: option.level,
          ...option,
        }))
        .sort((prev, next) => prev.value - next.value)
    } else {
      options = this.state.options.map((option) => ({
        value: option[field],
        label: option.description,
        level: option.level,
        ...option,
      }))
    }

    if (allOption) {
      options.unshift({ value: ALL, label: translate('option.all') })
    }

    return options
  }

  setValue = (value, query) => {
    const { options } = this.state
    let option
    switch (query.level) {
      case 2:
        option = options.find((option) => option.sectionId === value)
        return this.returnValue(value, option && option.description)
      case 3:
        option = options.find(
          (option) =>
            option.sectionId === query.sectionId &&
            option.subSectionId === value
        )
        return this.returnValue(value, option && option.description)
      case 4:
        option = options.find(
          (option) =>
            option.sectionId === query.sectionId &&
            option.subSectionId === query.subSectionId &&
            option.typeId === value
        )
        return this.returnValue(value, option && option.description)
      case 5:
        option = options.find(
          (option) =>
            option.sectionId === query.sectionId &&
            option.subSectionId === query.subSectionId &&
            option.typeId === query.typeId &&
            option.subTypeId === value
        )
        return this.returnValue(value, option && option.description)
      default:
        return null
    }
  }

  returnValue = (value, description) => {
    const { concatId } = this.props

    return concatId
      ? { value, label: `${value} - ${description}` }
      : { value, label: description }
  }

  handleChange = (selectedOptions) => {
    const { onChange, clearButton } = this.props

    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.some((option) => option.value === ALL)) {
        onChange(this.renderOptions())
        return
      }
    } else if (!selectedOptions && clearButton) {
      onChange({ value: '' })

      return
    }

    onChange(selectedOptions)
  }

  hasValue() {
    const { value } = this.props
    return (Array.isArray(value) && value.length > 0) || value
  }

  render() {
    const {
      placeholder,
      translate,
      query,
      clearButton,
      disabled = false,
      isMulti = false,
      noOptions,
      value,
      required,
      label,
      fill,
    } = this.props

    const options = this.renderOptions()

    let newValue = value

    const { loading } = this.state

    if (
      Array.isArray(newValue) &&
      newValue.length &&
      typeof newValue[0] === 'number'
    ) {
      newValue = newValue.map((cdSection) =>
        options.find((option) => option.sectionId === cdSection)
      )
    }

    if (typeof newValue === 'number') {
      newValue = this.setValue(newValue, query)
    }

    return (
      <Select
        rightIcon={
          this.hasValue() && clearButton
            ? 'glyph glyph-x hierarchy-clear-button'
            : ''
        }
        onRightIconClick={() => this.handleChange(isMulti ? [] : false)}
        label={label}
        options={noOptions ? [] : options}
        placeholder={translate(loading ? 'irc.loading' : placeholder)}
        disabled={loading ? true : disabled}
        isMulti={isMulti}
        translate={translate}
        onChange={this.handleChange}
        value={newValue}
        selectAll
        groupOptions
        required={required}
        fill={fill}
        validateValue={(val) =>
          isMulti ? val && val.length > 0 : Boolean(val)
        }
      />
    )
  }
}

const mapStateToProps = ({ auth }) => ({
  user: auth.user,
})

/*
 * Connects component to I18next
 */
const SectionSelectTranslated = translate(null, {
  wait: true,
  translateFuncName: 'translate',
})(HierarchySelect)

/*
 * Exports component connected to redux
 */
export default connect(mapStateToProps)(SectionSelectTranslated)

/*
 * Exports component only
 */
export { HierarchySelect as SectionSelect }
